import floor from 'lodash/floor';
import type {AudioPlayerObject, TrimData} from '@Libraries/audio-player/audio-player.types';
import {quantizeMediaTimeSeconds} from '@Utils/math.util';

/** The fields the trim math needs — so callers holding only a bar's trim data can use it too. */
export type TrimmedAudioFields = Pick<AudioPlayerObject, 'trim' | 'originalDuration' | 'speed'>;

/**
 * Twin of `AudioPlayerVO::hasUsableTrim()`: a stored trim window only counts when it can cut the
 * source. Posters saved from a corrupt trim can carry an end at or before the start; the backend
 * renders those clips untrimmed, so the editor must play and measure them the same way.
 */
export function hasUsableTrim(audioPlayerObject: TrimmedAudioFields): boolean {
	const {isTrimmed, startTime, endTime} = audioPlayerObject.trim;
	return isTrimmed && startTime >= 0 && endTime > startTime;
}

export function getTrimmedDuration(audioPlayerObject: TrimmedAudioFields): number {
	return getTrimmedEndTime(audioPlayerObject) - getTrimmedStartTime(audioPlayerObject);
}

export function getTrimmedPlaybackDuration(audioPlayerObject: TrimmedAudioFields): number {
	return getTrimmedDuration(audioPlayerObject) / audioPlayerObject.speed;
}

export function getPlaybackDuration(audioPlayerObject: TrimmedAudioFields): number {
	return getTrimmedPlaybackDuration(audioPlayerObject);
}

export function getMaxFadeDuration(audioPlayerObject: TrimmedAudioFields): number {
	if (getPlaybackDuration(audioPlayerObject) < 2) {
		return 0;
	}

	return floor(getPlaybackDuration(audioPlayerObject) / 2, 2);
}

export function getTrimmedStartTime(audioPlayerObject: TrimmedAudioFields): number {
	return hasUsableTrim(audioPlayerObject) ? audioPlayerObject.trim.startTime : 0;
}

export function getTrimmedEndTime(audioPlayerObject: TrimmedAudioFields): number {
	return hasUsableTrim(audioPlayerObject) ? audioPlayerObject.trim.endTime : audioPlayerObject.originalDuration;
}

/**
 * The trim that keeps a clip's playback within `maxPlaybackDuration` — the source window's end
 * pulled in by the overshoot at the clip's speed. A clip that already fits keeps its trim.
 */
export function getTrimFittingPlaybackDuration(audioPlayerObject: TrimmedAudioFields, maxPlaybackDuration: number): TrimData {
	if (getPlaybackDuration(audioPlayerObject) <= maxPlaybackDuration) {
		return audioPlayerObject.trim;
	}

	const startTime = getTrimmedStartTime(audioPlayerObject);
	return {
		isTrimmed: true,
		startTime,
		endTime: quantizeMediaTimeSeconds(startTime + maxPlaybackDuration * audioPlayerObject.speed),
	};
}
